<script setup lang="ts">
import type { NavItem } from '~~/shared/types/store'
import { useAuthStore } from '~~/stores/authStore'
import { useUIStore } from '~~/stores/uiStore'
import { ALL_CATEGORIES_HREF } from '~~/shared/utils/allCategoriesLink'

const props = defineProps<{
  items: NavItem[]
  isOpen: boolean
}>()

const emit = defineEmits<{ close: [] }>()

const auth = useAuthStore()
const ui = useUIStore()
// Sklep katalogowy nie zakłada kont — CTA logowania nie ma czego otworzyć.
const { sellingEnabled } = useCatalogOnly()
// Pozycje bez podkategorii jako linki zamiast zakładek — patrz BrandConfig.
const linkiBezposrednie = useBrand().mobileNavDirectLinks === true

// Nawigacja robi NuxtLink; tu tylko żądanie auto-otwarcia modala filtrów na
// listingu (konsumowane przez ProductFilters po załadowaniu facetów) + close.
function onAllCategoriesClick() {
  ui.requestMobileFiltersOpen(ALL_CATEGORIES_HREF)
  emit('close')
}

const activeTopSlug = ref<string | null>(null)
const openAccordion = ref<string | null>(null)

// Kolejność zakładek na mobile różni się od desktopu: „Nowości" ląduje zaraz za
// sekcją „Dziecko" (a nie na początku). Etykiety top-level są przypięte przez
// override'y kategorii, więc dopasowanie po labelu jest stabilne między
// środowiskami (slug bywa inny na QA vs prod). Slug „nowosci" jest stały.
const NOWOSCI_SLUG = 'nowosci'
const NOWOSCI_ANCHOR_LABEL = 'Dziecko'
const DEFAULT_TOP_LABEL = 'Kobieta'

const mobileItems = computed<NavItem[]>(() => {
  const nowosci = props.items.find(i => i.slug === NOWOSCI_SLUG)
  if (!nowosci) return props.items
  const rest = props.items.filter(i => i.slug !== NOWOSCI_SLUG)
  const anchorIdx = rest.findIndex(i => i.label === NOWOSCI_ANCHOR_LABEL)
  if (anchorIdx === -1) return props.items
  return [...rest.slice(0, anchorIdx + 1), nowosci, ...rest.slice(anchorIdx + 1)]
})

function maZawartosc(item: NavItem): boolean {
  return Boolean(item.groups?.length || item.children.length)
}

/**
 * Pozycja, która w pasku jest linkiem, a nie zakładką.
 *
 * Bez tego „Nowości" czy „Techniki znakowania" otwierały zakładkę z samą
 * swoją nazwą i przyciskiem „Zobacz wszystkie kategorie" — do strony trzeba
 * było stuknąć drugi raz. Tylko dla marek z `mobileNavDirectLinks`; reszta
 * zostaje przy zakładce z linkiem w treści.
 */
function jestLinkiem(item: NavItem): boolean {
  return linkiBezposrednie && !maZawartosc(item)
}

// Domyślnie otwarta zakładka to „Kobieta"; fallback do pierwszej pozycji.
// Gdy pozycje bez treści są linkami, nie mogą być zakładką domyślną — proles
// (bez „Kobiety") otwierał szufladę na „Nowościach", czyli na pustej treści.
const defaultTop = computed<NavItem | null>(() =>
  mobileItems.value.find(i => i.label === DEFAULT_TOP_LABEL)
  ?? (linkiBezposrednie ? mobileItems.value.find(maZawartosc) : undefined)
  ?? mobileItems.value[0]
  ?? null,
)

const activeTop = computed(() =>
  mobileItems.value.find(i => i.slug === activeTopSlug.value) ?? defaultTop.value
)

watch(() => props.isOpen, (val) => {
  if (val) {
    activeTopSlug.value = defaultTop.value?.slug ?? null
    openAccordion.value = null
  }
})

function selectTop(slug: string) {
  activeTopSlug.value = slug
  openAccordion.value = null
}

function toggleAccordion(slug: string) {
  openAccordion.value = openAccordion.value === slug ? null : slug
}
</script>

<template>
  <Transition name="drawer">
    <div v-if="isOpen" class="drawer" role="dialog" aria-modal="true">

      <!-- Horizontal top nav -->
      <div class="drawer__top-nav">
        <div class="drawer__top-nav-inner">
          <template v-for="item in mobileItems" :key="item.slug">
            <NuxtLink
              v-if="jestLinkiem(item)"
              :to="item.href"
              class="drawer__top-btn drawer__top-btn--link"
              @click="emit('close')"
            >
              {{ item.label }}
            </NuxtLink>
            <button
              v-else
              type="button"
              class="drawer__top-btn"
              :class="{ 'drawer__top-btn--active': item.slug === activeTop?.slug }"
              @click="selectTop(item.slug)"
            >
              {{ item.label }}
            </button>
          </template>
        </div>
      </div>

      <!-- Content: polecane + grupy/akordeony lub link bezpośredni -->
      <div class="drawer__content">
        <template v-if="activeTop && (activeTop.groups?.length || activeTop.children.length)">
          <!-- Polecane kategorie (sekcje modowe) -->
          <div v-if="activeTop.recommended?.length" class="drawer__reco">
            <p class="drawer__reco-cap">Polecane</p>
            <div class="drawer__reco-row">
              <NuxtLink
                v-for="r in activeTop.recommended"
                :key="r.id"
                :to="r.href"
                class="drawer__reco-tile"
                @click="emit('close')"
              >
                <span class="drawer__reco-thumb">
                  <img v-if="r.thumbnail" :src="r.thumbnail" :alt="r.label" class="drawer__reco-img" loading="lazy">
                  <span v-else class="drawer__reco-ph" aria-hidden="true">{{ r.label.trim().charAt(0).toUpperCase() }}</span>
                </span>
                <span class="drawer__reco-label">{{ r.label }}</span>
              </NuxtLink>
            </div>
          </div>

          <!-- Grupy po typie produktu (sekcje modowe) jako akordeony -->
          <template v-if="activeTop.groups?.length">
            <div
              v-for="g in activeTop.groups"
              :key="g.title"
              class="drawer__accordion"
            >
              <button
                type="button"
                class="drawer__accordion-header"
                :aria-expanded="openAccordion === g.title"
                @click="toggleAccordion(g.title)"
              >
                <span>{{ g.title }}</span>
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  class="drawer__chevron"
                  :class="{ 'drawer__chevron--open': openAccordion === g.title }"
                >
                  <path d="M1 1l4 4 4-4" />
                </svg>
              </button>
              <div v-show="openAccordion === g.title" class="drawer__accordion-body">
                <NuxtLink
                  v-for="sub in g.items"
                  :key="sub.href"
                  :to="sub.href"
                  class="drawer__sub-link"
                  @click="emit('close')"
                >
                  {{ sub.label }}
                </NuxtLink>
              </div>
            </div>
          </template>

          <!-- Dział niepogrupowany: children jako linki -->
          <template v-else>
            <NuxtLink
              v-for="child in activeTop.children"
              :key="child.href"
              :to="child.href"
              class="drawer__accordion-header drawer__accordion-header--link drawer__accordion"
              @click="emit('close')"
            >
              {{ child.label }}
            </NuxtLink>
          </template>
        </template>

        <!-- Link bezpośredni (Nowości, Marki, O nas, Kontakt — bez zawartości) -->
        <template v-else-if="activeTop">
          <NuxtLink
            :to="activeTop.href"
            class="drawer__direct-link"
            @click="emit('close')"
          >
            {{ activeTop.label }}
          </NuxtLink>
        </template>

        <NuxtLink
          :to="ALL_CATEGORIES_HREF"
          class="drawer__all-link"
          data-test="drawer-all-categories"
          @click="onAllCategoriesClick"
        >
          {{ $t('nav.viewAllCategories') }}
          <span aria-hidden="true">→</span>
        </NuxtLink>
      </div>

      <!-- Footer actions — tylko dla gościa; zalogowany nie widzi CTA logowania -->
      <div v-if="sellingEnabled && !auth.isLoggedIn" class="drawer__footer">
        <NuxtLink to="/auth" class="drawer__auth-btn" @click="emit('close')">
          Zaloguj się
        </NuxtLink>
        <span class="drawer__auth-sep">lub</span>
        <NuxtLink to="/auth?tab=register" class="drawer__auth-btn" @click="emit('close')">
          Załóż konto
        </NuxtLink>
      </div>

    </div>
  </Transition>
</template>

<style scoped>
.drawer {
  position: fixed;
  top: var(--header-height, 134px);
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-white);
  z-index: 98;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Transition */
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}

/* Top nav scroll */
.drawer__top-nav {
  border-bottom: 1px solid var(--color-border);
  overflow-x: auto;
  scrollbar-width: none;
  flex-shrink: 0;
}
.drawer__top-nav::-webkit-scrollbar { display: none; }

.drawer__top-nav-inner {
  display: flex;
  gap: 0;
  padding: 0 12px;
  white-space: nowrap;
}

.drawer__top-btn {
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  padding: 14px 12px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  transition: border-color 0.15s;
}

.drawer__top-btn--active {
  font-weight: var(--font-weight-normal);
  text-decoration: underline;
}

/* Link w pasku wygląda jak zakładka obok — różni się tylko tym, dokąd prowadzi. */
.drawer__top-btn--link {
  display: inline-block;
  text-decoration: none;
}

/* Accordion */
.drawer__content {
  flex: 1;
  padding: 0;
}

/* Polecane — przewijany wiersz kafli */
.drawer__reco {
  padding: 16px 20px 8px;
  border-bottom: 1px solid var(--color-border);
}

.drawer__reco-cap {
  margin: 0 0 12px;
  font-size: 0.6875rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-muted);
}

.drawer__reco-row {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scrollbar-width: none;
  padding-bottom: 4px;
}
.drawer__reco-row::-webkit-scrollbar { display: none; }

.drawer__reco-tile {
  flex: 0 0 108px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-decoration: none;
  color: inherit;
}

.drawer__reco-thumb {
  width: 108px;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background: var(--color-border);
  border-radius: var(--product-img-radius);
  display: flex;
  align-items: center;
  justify-content: center;
}

.drawer__reco-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.drawer__reco-ph {
  font-size: 1.5rem;
  color: var(--color-muted);
  opacity: 0.5;
}

.drawer__reco-label {
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1.25;
}

.drawer__accordion {
  border-bottom: 1px solid var(--color-border);
}

.drawer__accordion-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
  text-align: left;
  text-decoration: none;
}

.drawer__chevron {
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.drawer__chevron--open {
  transform: rotate(180deg);
}

.drawer__accordion-body {
  display: flex;
  flex-direction: column;
  padding: 0 20px 16px 20px;
  gap: 14px;
}

.drawer__sub-link {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1;
}

.drawer__direct-link {
  display: block;
  padding: 20px;
  font-size: 0.9375rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
}

.drawer__all-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 16px 20px;
  padding: 14px;
  border: 1px solid var(--color-black);
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-black);
  text-decoration: none;
}

/* Footer */
.drawer__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

.drawer__auth-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  height: 52px;
  border: 1px solid var(--color-black);
  border-radius: 38px;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  background: none;
  transition: background 0.15s, color 0.15s;
}

[data-brand="youlook"] .drawer__auth-btn {
  border-radius: 0;
}

.drawer__auth-btn:hover {
  background: var(--color-black);
  color: var(--color-white);
}

.drawer__auth-sep {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
}
</style>
